import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { inquiryService } from "@/features/inquiries/services/inquiryService";
import { companyService } from "@/features/company/services/companyService";
import { formatDisplayName } from "@/shared/utils/formatters";
import { Input } from "@/shared/components/ui/Input";
import { Button } from "@/shared/components/ui/Button";
import type {
  InquiryFormData,
  IssueOption,
  OfficerOption,
  CompanyOption,
  EmployeeOption,
} from "@/features/inquiries/types/inquiry";

const OFFICER_TYPES = ["officer", "admin"];

const STATUS_DB_MAP: Record<string, string> = {
  New: "NEW",
  "In Progress": "INPROG",
  Completed: "CLOSED",
};

const emptyForm: InquiryFormData = {
  title: "",
  description: "",
  type: "",
  occur_date: "",
  high_pri: false,
  watcher: [],
  resolution: "",
  closed_dt: null,
};

export const InquiryFormPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");

  const [form, setForm] = useState<InquiryFormData>({
    ...emptyForm,
    assigned_to: isOfficer ? String(userData?.id || "") : undefined,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Dropdown data
  const [issues, setIssues] = useState<IssueOption[]>([]);
  const [officers, setOfficers] = useState<OfficerOption[]>([]);

  // Company / Employee selection (officer creating on behalf of user)
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [companySearch, setCompanySearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [selectedEmployee, setSelectedEmployee] = useState<string>("");
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const employeeDropdownRef = useRef<HTMLDivElement>(null);

  // Watcher selection
  const [watcherDropdownOpen, setWatcherDropdownOpen] = useState(false);
  const [watcherSearch, setWatcherSearch] = useState("");
  const watcherDropdownRef = useRef<HTMLDivElement>(null);

  // Load dropdowns
  useEffect(() => {
    inquiryService.getActiveIssues(i18n.language).then(setIssues).catch(console.error);
    if (isOfficer) {
      inquiryService.getOfficersByBU(userData?.businessUnit || "").then(setOfficers).catch(console.error);
      companyService.getDropdown("ticketing").then(setCompanies).catch(console.error);
    }
  }, [i18n.language, isOfficer, userData?.businessUnit]);

  // Load employees when company changes
  useEffect(() => {
    if (!selectedCompany || !isOfficer) { setEmployees([]); return; }
    companyService
      .getEmployeesByCompany(selectedCompany)
      .then((data: any[]) => {
        setEmployees(data.map((emp: any) => ({
          value: emp.value || emp.user_id || emp.id,
          label: emp.label || formatDisplayName(emp.last_name, emp.first_name, emp.middle_name),
        })));
      })
      .catch(() => setEmployees([]));
  }, [selectedCompany, isOfficer]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node))
        setCompanyDropdownOpen(false);
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(e.target as Node))
        setEmployeeDropdownOpen(false);
      if (watcherDropdownRef.current && !watcherDropdownRef.current.contains(e.target as Node))
        setWatcherDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      if (type === "checkbox") {
        setForm(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
      } else {
        setForm(prev => ({ ...prev, [name]: value }));
      }
    },
    [],
  );

  const handleCompanySelect = (companyId: string) => {
    setSelectedCompany(companyId);
    setForm(prev => ({ ...prev, company: companyId, owner_id: undefined }));
    setSelectedEmployee("");
    setCompanyDropdownOpen(false);
    setCompanySearch("");
  };

  const handleEmployeeSelect = (employeeId: string) => {
    setSelectedEmployee(employeeId);
    setForm(prev => ({ ...prev, owner_id: employeeId }));
    setEmployeeDropdownOpen(false);
    setEmployeeSearch("");
  };

  const handleWatcherToggle = (officerId: string) => {
    setForm(prev => {
      const current = prev.watcher || [];
      const updated = current.includes(officerId)
        ? current.filter(w => w !== officerId)
        : [...current, officerId];
      return { ...prev, watcher: updated };
    });
  };

  const officerSelectionRequired = isOfficer;
  const officerSelectionComplete = !officerSelectionRequired || (!!selectedCompany && !!selectedEmployee);
  const fieldDisabled = !officerSelectionComplete;

  const validate = (): boolean => {
    if (officerSelectionRequired && !selectedCompany) { setError(t("inquiry.select_company")); return false; }
    if (officerSelectionRequired && !selectedEmployee) { setError(t("inquiry.select_ticket_owner")); return false; }
    if (!form.title.trim()) { setError(t("feed.form.required_fields")); return false; }
    if (!form.type) { setError(t("feed.form.required_fields")); return false; }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        status: STATUS_DB_MAP[form.status || "New"] || form.status,
      };
      await inquiryService.createInquiry(payload);
      navigate("/inquiries");
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || t("feed.form.save_error");
      setError(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const selectedCompanyLabel = companies.find(c => c.value === selectedCompany)?.label || "";
  const selectedEmployeeLabel = employees.find(e => e.value === selectedEmployee)?.label || "";

  return (
    <div className="max-w-2xl mx-auto">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 py-3 mb-4 md:mb-6 border-b border-gray-200">
        <div className="flex items-center justify-between gap-3 max-w-2xl mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/inquiries")}
              className="p-2 -ml-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg shrink-0"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
            <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate">
              {t("inquiry.actions.submit")}
            </h1>
          </div>
          <Button type="submit" form="inquiry-form" loading={saving} className="shrink-0">
            {t("inquiry.actions.submit")}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
        <form id="inquiry-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Officer: Company + Employee selection */}
          {isOfficer && (
            <div className="space-y-4 pb-4 border-b border-gray-200">
              {/* Company Search */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t("inquiry.select_company")} *
                </label>
                <div className="relative" ref={companyDropdownRef}>
                  <input
                    type="text"
                    value={companyDropdownOpen ? companySearch : selectedCompanyLabel}
                    onChange={e => { setCompanySearch(e.target.value); if (!companyDropdownOpen) setCompanyDropdownOpen(true); }}
                    onFocus={() => { setCompanyDropdownOpen(true); setCompanySearch(""); }}
                    placeholder={t("inquiry.search_company")}
                    className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {selectedCompany && !companyDropdownOpen && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCompany("");
                        setSelectedEmployee("");
                        setForm(prev => ({ ...prev, company: undefined, owner_id: undefined }));
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  {companyDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                      {companies
                        .filter(c => c.label?.toLowerCase().includes(companySearch.toLowerCase()))
                        .map(c => (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => handleCompanySelect(c.value)}
                            className={`w-full text-left px-3 py-2.5 md:py-2 text-base md:text-sm transition-colors ${
                              selectedCompany === c.value ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                      {companies.filter(c => c.label?.toLowerCase().includes(companySearch.toLowerCase())).length === 0 && (
                        <p className="px-3 py-2 text-xs text-gray-400 italic">{t("inquiry.no_results")}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Employee Selection */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t("inquiry.select_ticket_owner")} *
                </label>
                <div className="relative" ref={employeeDropdownRef}>
                  <input
                    type="text"
                    value={employeeDropdownOpen ? employeeSearch : selectedEmployeeLabel}
                    onChange={e => { setEmployeeSearch(e.target.value); if (!employeeDropdownOpen) setEmployeeDropdownOpen(true); }}
                    onFocus={() => { if (!selectedCompany) return; setEmployeeDropdownOpen(true); setEmployeeSearch(""); }}
                    disabled={!selectedCompany}
                    placeholder={selectedCompany ? t("inquiry.search_employee") : t("inquiry.select_company")}
                    className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      !selectedCompany ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300"
                    }`}
                  />
                  {employeeDropdownOpen && selectedCompany && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                      {employees
                        .filter(e => e.label?.toLowerCase().includes(employeeSearch.toLowerCase()))
                        .map(emp => (
                          <button
                            key={emp.value}
                            type="button"
                            onClick={() => handleEmployeeSelect(emp.value)}
                            className={`w-full text-left px-3 py-2.5 md:py-2 text-base md:text-sm transition-colors ${
                              selectedEmployee === emp.value ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {emp.label}
                          </button>
                        ))}
                      {employees.filter(e => e.label?.toLowerCase().includes(employeeSearch.toLowerCase())).length === 0 && (
                        <p className="px-3 py-2 text-xs text-gray-400 italic">{t("inquiry.no_results")}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!officerSelectionComplete && (
                <p className="text-xs text-amber-600 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
                  </svg>
                  {t("inquiry.select_company")} &amp; {t("inquiry.select_ticket_owner")}
                </p>
              )}
            </div>
          )}

          {/* Issue Type */}
          <div className="space-y-1">
            <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
              {t("inquiry.form.issue_type")} *
            </label>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              required
              disabled={fieldDisabled}
              className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                fieldDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300"
              }`}
            >
              <option value="">{t("inquiry.form.issue_type")}</option>
              {issues.map(issue => (
                <option key={issue.value} value={issue.value}>{issue.label}</option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <Input
            label={`${t("inquiry.form.subject")} *`}
            name="title"
            value={form.title}
            onChange={handleChange}
            required
            maxLength={100}
            disabled={fieldDisabled}
          />

          {/* Description */}
          <div className="space-y-1">
            <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
              {t("inquiry.form.description")}
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              disabled={fieldDisabled}
              className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                fieldDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300"
              }`}
            />
          </div>

          {/* Occur Date */}
          <Input
            label={t("inquiry.form.occur_date")}
            name="occur_date"
            type="date"
            value={form.occur_date || ""}
            onChange={handleChange}
            disabled={fieldDisabled}
          />

          {/* Officer-only fields */}
          {isOfficer && (
            <>
              {/* Assigned To */}
              <div className="space-y-1">
                <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                  {t("inquiry.form.assignee")}
                </label>
                <select
                  name="assigned_to"
                  value={form.assigned_to || ""}
                  onChange={handleChange}
                  disabled={fieldDisabled}
                  className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    fieldDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300"
                  }`}
                >
                  <option value="">{t("inquiry.form.select_officer")}</option>
                  {officers.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Watchers */}
              <div className="space-y-1">
                <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                  {t("inquiry.form.watchers")}
                </label>
                {form.watcher.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {form.watcher.map(wId => {
                      const match = officers.find(o => o.value === wId);
                      return (
                        <span key={wId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                          {match?.label || wId}
                          <button type="button" onClick={() => handleWatcherToggle(wId)} className="hover:text-indigo-900">&times;</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="relative" ref={watcherDropdownRef}>
                  <input
                    type="text"
                    value={watcherSearch}
                    onChange={e => { setWatcherSearch(e.target.value); if (!watcherDropdownOpen) setWatcherDropdownOpen(true); }}
                    onFocus={() => { if (!fieldDisabled) setWatcherDropdownOpen(true); }}
                    disabled={fieldDisabled}
                    placeholder={t("inquiry.form.add_watchers")}
                    className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      fieldDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300"
                    }`}
                  />
                  {watcherDropdownOpen && !fieldDisabled && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                      {officers
                        .filter(o => o.label?.toLowerCase().includes(watcherSearch.toLowerCase()))
                        .map(o => {
                          const isSelected = form.watcher.includes(o.value);
                          return (
                            <button
                              key={o.value}
                              type="button"
                              onClick={() => { handleWatcherToggle(o.value); setWatcherSearch(""); }}
                              className={`w-full flex items-center gap-2 px-3 py-2.5 md:py-2 text-base md:text-sm transition-colors ${
                                isSelected ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${
                                isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"
                              }`}>
                                {isSelected && "✓"}
                              </span>
                              {o.label}
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>

              {/* High Priority */}
              <label className={`flex items-center gap-2 text-sm ${fieldDisabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700"}`}>
                <input
                  type="checkbox"
                  name="high_pri"
                  checked={form.high_pri}
                  onChange={handleChange}
                  disabled={fieldDisabled}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed"
                />
                {t("inquiry.form.priority")}
              </label>
            </>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </form>
      </div>
    </div>
  );
};
